import { useCallback } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import {
  IIncidentCreateMutation,
  IIncidentCreateMutationVariables,
  IIncidentEditMutation,
  IIncidentEditMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../../utils/types';
import {
  addNewAnalysisIncidentThunk,
  editAnalysisIncidentThunk,
  updateIncidentModalDataThunk,
} from '../../../../redux/slices/analyses';
import {
  addNewIncidentAction,
  updateIncidentAction,
} from '../../../../redux/actions/incidents';
import {
  setDictionaryListAction,
  updateDictionaryItemAction,
} from '../../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../../utils/interfaces';
import incidentCreateMutation from '../../../../pages/Incidents/apollo/incidentCreateMutation';
import incidentEditMutation from '../../../../pages/Incidents/apollo/incidentEditMutation';
import { IIncident } from '../../../../pages/Incidents/interfaces';
import { RootState } from '../../../../redux/store';
import { useBootstrapSelector } from '../../../../redux/selectorHooks';
import toastMessage from '../../../../utils/toastMessage';

export const isDateBefore = (date1: Date, date2: Date) => {
  return date1.getTime() <= date2.getTime();
};

const dictionaryType = DictionaryTypeEnum.CalendarIncident;

export default (analysisId?: string) => {
  const dispatch = useDispatch();

  const defaultConfig = useBootstrapSelector('defaultConfig');
  assertNotNil(defaultConfig);
  const { defaultEnd } = defaultConfig;

  const threeMonthsAgo = moment(defaultEnd).subtract(3, 'months').toDate();

  const isIncidentWithinDateRange = useCallback(
    (entity: IIncident) => {
      const start = new Date(entity.start);
      const end = new Date(entity.end);
      const dateRangeStart = threeMonthsAgo;
      const dateRangeEnd = new Date(defaultEnd);
      return (
        (isDateBefore(dateRangeStart, start) &&
          isDateBefore(start, dateRangeEnd)) ||
        (isDateBefore(dateRangeStart, end) && isDateBefore(end, dateRangeEnd))
      );
    },
    [threeMonthsAgo, defaultEnd],
  );

  const incidents: IIncident[] = useSelector(
    (state: RootState) => state.dictionary.calendarIncident,
  );

  const onCreate = useCallback(
    ({ incidentCreate }: IIncidentCreateMutation) => {
      const { entity, errors } = incidentCreate;
      if (printGraphqlErrors(errors)) {
        return;
      }

      assertNotNil(entity);

      if (analysisId) {
        dispatch(addNewAnalysisIncidentThunk({ incident: entity, analysisId }));
      }

      dispatch(addNewIncidentAction(entity));

      // if the new incident date is within the default date range in
      // the incidents list,
      // add new incident to existing list in redux
      if (isIncidentWithinDateRange(entity)) {
        dispatch(
          setDictionaryListAction({
            newValues: [...incidents, entity],
            dictionaryType,
          }),
        );
      }

      toastMessage({
        message: `${entity.name} has been created`,
        type: 'info',
      });
    },
    [analysisId, dispatch, incidents, isIncidentWithinDateRange],
  );

  const [createIncident, { loading: isCreateIncidentLoading }] = useMutation<
    IIncidentCreateMutation,
    IIncidentCreateMutationVariables
  >(incidentCreateMutation, {
    onCompleted: onCreate,
  });

  const onEdit = useCallback(
    ({ incidentEdit }: IIncidentEditMutation) => {
      const { entity, errors } = incidentEdit;
      if (printGraphqlErrors(errors)) {
        return;
      }
      assertNotNil(entity);
      if (analysisId) {
        dispatch(editAnalysisIncidentThunk({ incident: entity, analysisId }));
      }
      dispatch(updateIncidentAction(entity));
      dispatch(
        updateDictionaryItemAction({
          item: entity,
          dictionaryType,
        }),
      );
      // if new incident date is outside the default date range of incident list,
      // remove this incident from existing list
      if (!isIncidentWithinDateRange(entity)) {
        const newValues = incidents.filter((v) => v.id !== entity.id);
        dispatch(
          setDictionaryListAction({
            newValues,
            dictionaryType,
          }),
        );
      }
      dispatch(updateIncidentModalDataThunk({ incident: entity }));

      toastMessage({
        message: `${entity.name} has been updated`,
        type: 'info',
      });
    },
    [analysisId, dispatch, incidents, isIncidentWithinDateRange],
  );

  const [editIncident, { loading: isEditIncidentLoading }] = useMutation<
    IIncidentEditMutation,
    IIncidentEditMutationVariables
  >(incidentEditMutation, {
    onCompleted: onEdit,
  });

  return {
    createIncident,
    isCreateIncidentLoading,
    editIncident,
    isEditIncidentLoading,
  };
};
